import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, Center, Group, Loader } from '@mantine/core';
import { FormattedMessage } from 'react-intl';
import { PaymentMethod, TransactionCategory } from '@shipstation/financial-services-ui-components';

import { ModalContent } from '@shipstation/client-common/components/modal-content';
import { ModalFooter } from '@shipstation/client-common/components/modal-footer';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

import { FinancialServicesUIProviderWrapper } from '../../../financial-services-ui/financial-services-ui-provider-wrapper';
import FsuiContextProvider, { SOURCE_PURCHASE_SHIPMENT_VOLUME } from '../../../financial-services-ui/fsui-context-provider';
import { getBillingInfoFromPaymentMethod } from '../../../financial-services-ui/mapping';
import { messages as fsuiMessages } from '../../../financial-services-ui/messages';
import { usePaymentAndSubscriptionMegaContext } from '../../../pages/settings/payment-and-subscription/context';
import Global from '../../../store/global-selectors';

import { PaymentMethodForm } from './payment-method-form';
import { usePaymentMethodChange } from './use-payment-method-change';
import { messages } from '../../purchase-shipment-volume/messages';

import type { SellerBillingFields } from './use-payment-method-change';

const MIN_LOADING_HEIGHT_PX = 250;

const payByBankTypes = [PaymentMethod.PayByBankEU, PaymentMethod.PayByBankUS];

type PaymentMethodChangeViewProps = {
  selectedQuantity: number | null;
  onBack: () => void;
};

const PaymentMethodChangeBody = ({ selectedQuantity, onBack }: PaymentMethodChangeViewProps) => {
  const {
    isAddingNew,
    toggleAddingNew,
    isPreloadingSession,
    hasSessionError,
    isSubmitting,
    paymentMethodDetails,
    subscriptionPaymentMethod,
    selectSavedMethod,
    submitNewMethod,
    paymentType,
    setPaymentType,
    creditCardConfig,
    payByBankUsConfig,
    handleSetPerformFormValidation,
    preloadedSession,
    locale,
  } = usePaymentMethodChange({ onDone: onBack, selectedQuantity });

  const currencyCode = useSelector(Global.selectSellerSubscriptionCurrency);
  const submitLabel =
    isAddingNew && !!paymentType && payByBankTypes.includes(paymentType) ? fsuiMessages.continueToBank : messages.submitPaymentMethod;

  if (isPreloadingSession || (!preloadedSession && !hasSessionError)) {
    return (
      <ModalContent>
        <Center mih={MIN_LOADING_HEIGHT_PX}>
          <Loader />
        </Center>
      </ModalContent>
    );
  }

  return (
    <>
      <ModalContent>
        <PaymentMethodForm
          creditCardConfig={creditCardConfig}
          currencyCode={currencyCode}
          eligiblePaymentMethodTypes={preloadedSession?.eligiblePaymentMethodTypes ?? (emptyArray as PaymentMethod[])}
          isAddingNew={isAddingNew}
          isSubmitting={isSubmitting}
          locale={locale}
          onPaymentTypeChange={setPaymentType}
          onSelectSavedMethod={selectSavedMethod}
          onSetPerformFormValidation={handleSetPerformFormValidation}
          onToggleAddingNew={toggleAddingNew}
          payByBankUsConfig={payByBankUsConfig}
          paymentMethodDetails={paymentMethodDetails ?? emptyArray}
          paymentType={paymentType}
          subscriptionPaymentMethod={subscriptionPaymentMethod}
        />
      </ModalContent>
      <ModalFooter>
        <Group justify='space-between' w='100%'>
          <Button disabled={isSubmitting} onClick={onBack} variant='ghost'>
            <FormattedMessage {...messages.backButton} />
          </Button>
          {isAddingNew ? (
            <Button loading={isSubmitting} onClick={submitNewMethod} variant='primary'>
              <FormattedMessage {...submitLabel} />
            </Button>
          ) : null}
        </Group>
      </ModalFooter>
    </>
  );
};

export const PaymentMethodChangeView = ({ selectedQuantity, onBack }: PaymentMethodChangeViewProps) => {
  const sellerCurrencyCode = useSelector(Global.selectSellerSubscriptionCurrency);
  const sellerState = useSelector((state: { seller: SellerBillingFields }) => state.seller) ?? emptyObject;
  const { name, homeCountry, phone, companyName } = sellerState;
  const { paymentMethodDetails } = usePaymentAndSubscriptionMegaContext();

  const initialBillingInfo = useMemo(() => {
    const subscriptionMethod = paymentMethodDetails?.find((method) =>
      method.assignedPreferences?.includes(TransactionCategory.Subscription),
    );
    return getBillingInfoFromPaymentMethod(subscriptionMethod, { name, homeCountry, phone, companyName }) ?? undefined;
  }, [paymentMethodDetails, name, homeCountry, phone, companyName]);

  return (
    <FsuiContextProvider
      addtlReturnParams={`${SOURCE_PURCHASE_SHIPMENT_VOLUME}&transactionCategory=${TransactionCategory.Subscription}`}
      currencyCode={sellerCurrencyCode}
      transactionCategory={TransactionCategory.Subscription}
    >
      <FinancialServicesUIProviderWrapper initialBillingInfo={initialBillingInfo}>
        <PaymentMethodChangeBody onBack={onBack} selectedQuantity={selectedQuantity} />
      </FinancialServicesUIProviderWrapper>
    </FsuiContextProvider>
  );
};
